<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <h1 class="h2 mb-1 fw-bold">Drones</h1>
    <p class="text-muted mb-0">Manage your drone fleet</p>
  </div>
  <button class="btn btn-primary" (click)="addDrone()">
    <i class="bi bi-plus-lg me-2"></i>Add Drone
  </button>
</div>

<!-- Stats Cards -->
<div class="row g-3 mb-4">
  <div class="col-12 col-sm-6 col-lg-3">
    <div class="card stat-card h-100">
      <div class="card-body p-3">
        <div class="d-flex align-items-center gap-3">
          <div class="stat-icon bg-primary-subtle text-primary">
            <i class="icon-drone"></i>
          </div>
          <div>
            <div class="stat-value text-primary">{{ totalDrones() }}</div>
            <div class="stat-label text-muted">Total Drones</div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="col-12 col-sm-6 col-lg-3">
    <div class="card stat-card h-100">
      <div class="card-body p-3">
        <div class="d-flex align-items-center gap-3">
          <div class="stat-icon bg-success-subtle text-success">
            <i class="bi bi-check-circle"></i>
          </div>
          <div>
            <div class="stat-value text-success">{{ activeDrones() }}</div>
            <div class="stat-label text-muted">Active Drones</div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="col-12 col-sm-6 col-lg-3">
    <div class="card stat-card h-100">
      <div class="card-body p-3">
        <div class="d-flex align-items-center gap-3">
          <div class="stat-icon bg-info-subtle text-info">
            <i class="bi bi-clock-history"></i>
          </div>
          <div>
            <div class="stat-value text-info">{{ totalFlightHours() | number:'1.1-1' }}</div>
            <div class="stat-label text-muted">Flight Hours</div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="col-12 col-sm-6 col-lg-3">
    <div class="card stat-card h-100">
      <div class="card-body p-3">
        <div class="d-flex align-items-center gap-3">
          <div class="stat-icon bg-warning-subtle text-warning">
            <i class="bi bi-rulers"></i>
          </div>
          <div>
            <div class="stat-value text-warning">{{ totalAcresSprayed() | number:'1.0-0' }}</div>
            <div class="stat-label text-muted">Acres Sprayed</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Filters -->
<div class="card mb-4">
  <div class="card-body p-3">
    <div class="row g-3 align-items-end">
      <div class="col-md-4">
        <label class="form-label">Search</label>
        <div class="input-group">
          <span class="input-group-text"><i class="bi bi-search"></i></span>
          <input type="text" class="form-control" placeholder="Search by name, serial, model..."
                 [ngModel]="searchQuery()" (ngModelChange)="searchQuery.set($event)">
        </div>
      </div>
      <div class="col-md-3">
        <label class="form-label">Ownership</label>
        <select class="form-select" [ngModel]="ownershipFilter()" (ngModelChange)="ownershipFilter.set($event)">
          <option value="all">All Drones</option>
          <option value="adn">ADN Leased</option>
          <option value="organization">Organization Owned</option>
        </select>
      </div>
      <div class="col-md-3">
        <label class="form-label">Status</label>
        <select class="form-select" [ngModel]="statusFilter()" (ngModelChange)="statusFilter.set($event)">
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="maintenance">Maintenance</option>
          <option value="inactive">Inactive</option>
          <option value="retired">Retired</option>
        </select>
      </div>
      <div class="col-md-2">
        <button class="btn btn-outline-secondary w-100" (click)="clearFilters()"
                [disabled]="!hasActiveFilters()">
          <i class="bi bi-x-lg me-1"></i>Clear
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Drones List -->
<div class="card">
  <div class="card-header d-flex justify-content-between align-items-center p-3">
    <h5 class="mb-0">Drones</h5>
    <span class="badge bg-secondary">{{ filteredDrones().length }} drone{{ filteredDrones().length !== 1 ? 's' : '' }}</span>
  </div>
  <div class="card-body p-0">
    <ng-container *ngIf="filteredDrones().length > 0; else emptyState">
      <div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead>
            <tr>
              <th>Drone</th>
              <th>Serial Number</th>
              <th>Status</th>
              <th class="text-end">Flights</th>
              <th class="text-end">Acres</th>
              <th>Last Flight</th>
              <th class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let drone of filteredDrones()">
              <td>
                <div class="d-flex align-items-center gap-2">
                  <div class="drone-icon">
                    <i class="icon-drone-fill"></i>
                  </div>
                  <div>
                    <div class="d-flex align-items-center gap-2">
                      <span class="fw-medium">{{ drone.name }}</span>
                      <span class="badge" [ngClass]="getOwnershipClass(drone.ownership)">
                        {{ formatOwnership(drone.ownership) }}
                      </span>
                    </div>
                    <div class="small text-muted">
                      {{ drone.manufacturerName }} {{ drone.modelName }}
                    </div>
                  </div>
                </div>
              </td>
              <td>
                <code class="small">{{ drone.serialNumber }}</code>
              </td>
              <td>
                <span class="badge" [ngClass]="getStatusClass(drone.status)">
                  {{ formatStatus(drone.status) }}
                </span>
              </td>
              <td class="text-end">
                <span class="fw-medium">{{ getDroneStats(drone.id).totalFlights }}</span>
              </td>
              <td class="text-end">
                {{ getDroneStats(drone.id).totalAcresSprayed | number:'1.0-0' }}
              </td>
              <td>
                <span [ngClass]="{'text-muted': !getDroneStats(drone.id).lastFlightDate}">
                  {{ getDroneStats(drone.id).lastFlightDate ? (getDroneStats(drone.id).lastFlightDate | adnDate) : 'Never' }}
                </span>
              </td>
              <td class="text-end">
                <div class="btn-group btn-group-sm">
                  <button class="btn btn-outline-secondary" (click)="viewDrone(drone)" title="View">
                    <i class="bi bi-eye"></i>
                  </button>
                  <button class="btn btn-outline-secondary"
                          (click)="editDrone(drone)"
                          [disabled]="!canEdit(drone)"
                          [title]="canEdit(drone) ? 'Edit' : 'ADN drones cannot be edited'">
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button class="btn btn-outline-secondary text-danger"
                          (click)="deleteDrone(drone)"
                          [disabled]="!canDelete(drone)"
                          [title]="canDelete(drone) ? 'Delete' : 'ADN drones cannot be deleted'">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ng-container>

    <ng-template #emptyState>
      <div class="p-5 text-center">
        <div class="empty-state-icon mb-3">
          <i class="icon-drone"></i>
        </div>
        <h5>No Drones Yet</h5>
        <p class="text-muted mb-3" *ngIf="!hasActiveFilters()">
          Add your first drone or contact ADN about leasing options.
        </p>
        <p class="text-muted mb-3" *ngIf="hasActiveFilters()">
          No drones match your filters. Try adjusting your criteria.
        </p>
        <button class="btn btn-primary" (click)="addDrone()" *ngIf="!hasActiveFilters()">
          <i class="bi bi-plus-lg me-2"></i>Add Drone
        </button>
        <button class="btn btn-outline-secondary" (click)="clearFilters()" *ngIf="hasActiveFilters()">
          <i class="bi bi-x-lg me-2"></i>Clear Filters
        </button>
      </div>
    </ng-template>
  </div>
</div>

<!-- Confirm Delete Modal -->
<app-confirm-modal
  #confirmModal
  (confirmed)="onDeleteConfirmed()"
  (cancelled)="onDeleteCancelled()"
></app-confirm-modal>
